/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";

import type { User } from "@src/@types/User";
import type { Project } from "@src/@types/Project";
import type { Endpoint as EndpointType } from "@src/@types/Endpoint";

import Dropdown from "@src/components/ui/Dropdowns/Dropdown";
import NewItemDropdown from "@src/components/ui/Dropdowns/NewItemDropdown";
import Modal from "@src/components/ui/Modal";
import ChooseProvider from "@src/components/modules/EndpointModule/ChooseProvider";
import EndpointModal from "@src/components/modules/EndpointModule/EndpointModal";
import UserModal from "@src/components/modules/UserModule/UserModal";
import ProjectModal from "@src/components/modules/ProjectModule/ProjectModal";

import projectStore from "@src/stores/ProjectStore";
import userStore from "@src/stores/UserStore";
import endpointStore from "@src/stores/EndpointStore";
import notificationStore from "@src/stores/NotificationStore";
import providerStore from "@src/stores/ProviderStore";
import { ThemePalette } from "@src/components/Theme";
import { ProviderTypes } from "@src/@types/Providers";
import MinionEndpointModal from "@src/components/modules/MinionModule/MinionEndpointModal";
import MinionPoolModal from "@src/components/modules/MinionModule/MinionPoolModal";
import ObjectUtils from "@src/utils/ObjectUtils";
import regionStore from "@src/stores/RegionStore";
import Config from "@src/utils/Config";

/*
 * The Ops Console page header (the Nutanix hub's title row): a 24 px semibold title with an
 * optional status pill, one line of description under it, and on the right the project selector
 * and the "New" menu. Notifications and the user menu live in the top bar and the sidebar.
 */
const PILL_TONES = {
  success: { bg: "var(--t9-success-50)", fg: "var(--t9-success-700)" }, // --t9-success-50 / 700
  info: { bg: "var(--t9-info-50)", fg: "var(--t9-info-700)" }, // --t9-info-50 / 700
  warning: { bg: "var(--t9-warning-50)", fg: "var(--t9-warning-700)" }, // --t9-warning-50 / 700
  danger: { bg: "var(--t9-danger-50)", fg: "var(--t9-danger-700)" }, // --t9-danger-50 / 700
  neutral: { bg: ThemePalette.grayscale[1], fg: ThemePalette.grayscale[4] },
};
export type PillTone = keyof typeof PILL_TONES;

const Wrapper = styled.div<any>`
  display: flex;
  margin: 24px 0 20px 0;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px 16px;
`;
const Heading = styled.div`
  flex: 1 1 320px;
  min-width: 0;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const Title = styled.h1<any>`
  margin: 0;
  color: ${ThemePalette.black};
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  letter-spacing: -0.01em;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Pill = styled.span<{ tone: PillTone }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  background: ${props => PILL_TONES[props.tone].bg};
  color: ${props => PILL_TONES[props.tone].fg};
`;
const Subtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  line-height: 20px;
  color: ${ThemePalette.grayscale[5]};
`;
const Controls = styled.div<any>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 0;
`;

type Props = {
  title: string;
  /** One line under the title saying what the page is for. */
  subtitle?: string;
  /** A status pill beside the title ("Connected"). */
  pill?: string;
  pillTone?: PillTone;
  onProjectChange?: (project: Project) => void;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  componentRef?: (ref: any) => void;
};
type State = {
  showChooseProviderModal: boolean;
  showEndpointModal: boolean;
  showChooseMinionEndpointModal: boolean;
  showMinionPoolModal: boolean;
  selectedMinionPoolEndpoint: EndpointType | null;
  showUserModal: boolean;
  showProjectModal: boolean;
  providerType: ProviderTypes | null;
  uploadedEndpoint: EndpointType | null;
  multiValidating: boolean;
  selectedMinionPoolPlatform: "source" | "destination";
  addingProject: boolean;
};
@observer
class PageHeader extends React.Component<Props, State> {
  state: State = {
    showChooseProviderModal: false,
    showEndpointModal: false,
    showChooseMinionEndpointModal: false,
    selectedMinionPoolEndpoint: null,
    showMinionPoolModal: false,
    showUserModal: false,
    showProjectModal: false,
    providerType: null,
    uploadedEndpoint: null,
    multiValidating: false,
    selectedMinionPoolPlatform: "source",
    addingProject: false,
  };

  UNSAFE_componentWillMount() {
    if (this.props.componentRef) {
      this.props.componentRef(this);
    }
  }

  componentDidMount() {
    // Pages that don't load projects themselves (dashboard, live monitor, change control, audit)
    // still need the project selector filled, or it stays on "Loading...".
    if (!projectStore.projects.length) {
      projectStore.getProjects();
    }
  }

  getCurrentProject() {
    const project =
      userStore.loggedUser && userStore.loggedUser.project
        ? userStore.loggedUser.project
        : null;
    if (project) {
      return projectStore.projects.find(p => p.id === project.id);
    }

    return null;
  }

  handleNewItem(item: string | null | undefined) {
    switch (item) {
      case "endpoint":
        providerStore.loadProviders();
        regionStore.getRegions();
        if (this.props.onModalOpen) {
          this.props.onModalOpen();
        }
        this.setState({ showChooseProviderModal: true });
        break;
      case "minionPool":
        providerStore.loadProviders();
        endpointStore.getEndpoints({ showLoading: true });
        if (this.props.onModalOpen) {
          this.props.onModalOpen();
        }
        this.setState({ showChooseMinionEndpointModal: true });
        break;
      case "user":
        projectStore.getProjects();
        if (this.props.onModalOpen) {
          this.props.onModalOpen();
        }
        this.setState({ showUserModal: true });
        break;
      case "project":
        if (this.props.onModalOpen) {
          this.props.onModalOpen();
        }
        endpointStore.getEndpoints();
        this.setState({ showProjectModal: true });
        break;
      default:
    }
  }

  handleCloseChooseProviderModal() {
    if (this.props.onModalClose) {
      this.props.onModalClose();
    }
    this.setState({ showChooseProviderModal: false });
  }

  handleCloseChooseMinionPoolEndpointModal() {
    if (this.props.onModalClose) {
      this.props.onModalClose();
    }
    this.setState({ showChooseMinionEndpointModal: false });
  }

  handleBackMinionPoolModal() {
    this.setState({
      showChooseMinionEndpointModal: true,
      showMinionPoolModal: false,
    });
  }

  handleCloseMinionPoolModalRequest() {
    if (this.props.onModalClose) {
      this.props.onModalClose();
    }
    this.setState({ showMinionPoolModal: false });
  }

  handleChooseMinionPoolSelectEndpoint(
    selectedMinionPoolEndpoint: EndpointType,
    platform: "source" | "destination",
  ) {
    this.setState({
      showChooseMinionEndpointModal: false,
      showMinionPoolModal: true,
      selectedMinionPoolEndpoint,
      selectedMinionPoolPlatform: platform,
    });
  }

  handleProviderClick(providerType: ProviderTypes) {
    this.setState({
      showChooseProviderModal: false,
      showEndpointModal: true,
      uploadedEndpoint: null,
      providerType,
    });
  }

  handleUploadEndpoint(endpoint: EndpointType) {
    endpointStore.setConnectionInfo(endpoint.connection_info);
    this.setState({
      showChooseProviderModal: false,
      showEndpointModal: true,
      providerType: endpoint.type,
      uploadedEndpoint: endpoint,
    });
  }

  handleRemoveEndpoint(endpoint: EndpointType) {
    endpointStore.delete(endpoint);
  }

  async handleValidateMultipleEndpoints(endpoints: EndpointType[]) {
    this.setState({ multiValidating: true });
    const addedEndpoints = await endpointStore.addMultiple(endpoints);
    await endpointStore.validateMultiple(addedEndpoints);
    this.setState({ multiValidating: false });
  }

  handleResetValidation() {
    endpointStore.resetMultiValidation();
  }

  handleCloseEndpointModal() {
    if (this.props.onModalClose) {
      this.props.onModalClose();
    }
    this.setState({ showEndpointModal: false });
  }

  handleBackEndpointModal(options?: { autoClose?: boolean }) {
    const showChooseProviderModal = !options || !options.autoClose;
    this.setState({ showChooseProviderModal, showEndpointModal: false });
  }

  async handleProjectChange(project: Project) {
    await userStore.switchProject(project.id);
    await projectStore.getProjects();
    notificationStore.loadData(true);

    if (this.props.onProjectChange) {
      this.props.onProjectChange(project);
    }
  }

  handleUserModalClose() {
    if (this.props.onModalClose) {
      this.props.onModalClose();
    }
    this.setState({ showUserModal: false });
  }

  async handleUserUpdateClick(user: User) {
    await userStore.add(user);
    if (this.props.onModalClose) {
      this.props.onModalClose();
    }
    this.setState({ showUserModal: false });
  }

  handleProjectModalClose() {
    if (this.props.onModalClose) {
      this.props.onModalClose();
    }
    this.setState({ showProjectModal: false });
  }

  async handleProjectModalUpdateClick(project: Project) {
    try {
      this.setState({ addingProject: true });
      const newProject = await projectStore.add(project);

      const bareMetalEndpoint = endpointStore.endpoints.find(
        e => e.name === Config.config.bareMetalEndpointName,
      );
      if (bareMetalEndpoint) {
        await endpointStore.duplicate({
          shouldSwitchProject: true,
          onSwitchProject: () => userStore.switchProject(newProject.id),
          endpoints: [bareMetalEndpoint],
        });
      }
    } finally {
      if (this.props.onModalClose) {
        this.props.onModalClose();
      }
      this.setState({ showProjectModal: false, addingProject: false });
    }
  }

  render() {
    return (
      <Wrapper>
        <Heading>
          <TitleRow>
            <Title title={this.props.title}>{this.props.title}</Title>
            {this.props.pill ? (
              <Pill tone={this.props.pillTone || "success"}>{this.props.pill}</Pill>
            ) : null}
          </TitleRow>
          {this.props.subtitle ? <Subtitle>{this.props.subtitle}</Subtitle> : null}
        </Heading>
        <Controls>
          <Dropdown
            selectedItem={this.getCurrentProject()}
            items={projectStore.projects}
            onChange={project => {
              this.handleProjectChange(project);
            }}
            noItemsMessage="Loading..."
            labelField="name"
          />
          <NewItemDropdown
            onChange={item => {
              this.handleNewItem(item.value);
            }}
          />
        </Controls>
        <Modal
          isOpen={this.state.showChooseProviderModal}
          title="New cloud endpoint"
          onRequestClose={() => {
            this.handleCloseChooseProviderModal();
          }}
        >
          <ChooseProvider
            onCancelClick={() => {
              this.handleCloseChooseProviderModal();
            }}
            providers={providerStore.providerNames}
            loading={providerStore.providersLoading || regionStore.loading}
            regions={regionStore.regions}
            onProviderClick={providerName => {
              this.handleProviderClick(providerName);
            }}
            onUploadEndpoint={endpoint => {
              this.handleUploadEndpoint(endpoint);
            }}
            multiValidating={this.state.multiValidating}
            onValidateMultipleEndpoints={endpoints => {
              this.handleValidateMultipleEndpoints(endpoints);
            }}
            multiValidation={endpointStore.multiValidation}
            onRemoveEndpoint={e => {
              this.handleRemoveEndpoint(e);
            }}
            onResetValidation={() => {
              this.handleResetValidation();
            }}
          />
        </Modal>
        {this.state.showChooseMinionEndpointModal ? (
          <MinionEndpointModal
            providers={providerStore.providers}
            endpoints={endpointStore.endpoints}
            loading={providerStore.providersLoading || endpointStore.loading}
            onRequestClose={() => {
              this.handleCloseChooseMinionPoolEndpointModal();
            }}
            onSelectEndpoint={(endpoint, platform) => {
              this.handleChooseMinionPoolSelectEndpoint(endpoint, platform);
            }}
          />
        ) : null}
        {this.state.showMinionPoolModal ? (
          <Modal
            isOpen
            title={`New ${ObjectUtils.capitalizeFirstLetter(
              this.state.selectedMinionPoolPlatform,
            )} SwiftIQ Agent pool`}
            onRequestClose={() => {
              this.handleCloseMinionPoolModalRequest();
            }}
          >
            <MinionPoolModal
              cancelButtonText="Back"
              platform={this.state.selectedMinionPoolPlatform}
              endpoint={this.state.selectedMinionPoolEndpoint!}
              onCancelClick={() => {
                this.handleBackMinionPoolModal();
              }}
              onRequestClose={() => {
                this.handleCloseMinionPoolModalRequest();
              }}
            />
          </Modal>
        ) : null}
        {this.state.showEndpointModal && this.state.providerType ? (
          <Modal
            isOpen
            title="New cloud endpoint"
            onRequestClose={() => {
              this.handleCloseEndpointModal();
            }}
          >
            <EndpointModal
              type={this.state.providerType}
              cancelButtonText="Back"
              onCancelClick={options => {
                this.handleBackEndpointModal(options);
              }}
              endpoint={this.state.uploadedEndpoint}
              isNewEndpoint={Boolean(this.state.uploadedEndpoint)}
            />
          </Modal>
        ) : null}
        {this.state.showUserModal ? (
          <UserModal
            isNewUser
            loading={userStore.updating}
            projects={projectStore.projects}
            onRequestClose={() => {
              this.handleUserModalClose();
            }}
            onUpdateClick={user => {
              this.handleUserUpdateClick(user);
            }}
          />
        ) : null}
        {this.state.showProjectModal ? (
          <ProjectModal
            isNewProject
            loading={this.state.addingProject}
            onRequestClose={() => {
              this.handleProjectModalClose();
            }}
            onUpdateClick={project => {
              this.handleProjectModalUpdateClick(project);
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

export default PageHeader;
